// src/libs/trade-in/trade-in.ts
//
// TactSuit X40·TactSuit X16 보상판매(#6101) — mgmt `/api/v1/trade-in/…` 응답 타입과 해석.
// (경로에 `/` + `*` 를 붙여 쓰지 않는다 — 줄 주석 안이어도 블록 주석 제거 정규식이 시작점으로 잡는다.)
//
// 정본은 #6101 코멘트 「mgmt API 계약 v0」(6489422877)과 그 보강(6493991916 · 6538120088)이고, 타입은 mgmt
// `tradein/TradeInDtos.kt`·`TradeInModels.kt` 를 그대로 옮겼다(2026-11-02 `origin/main` 기준). 이 모듈은 **해석만** 한다 —
// HTTP 응답을 화면이 그릴 수 있는 결과 하나로 바꾸고, 모르는 값은 「일반 오류」로 떨어뜨린다(계약: 「모르는 열거값은 일반
// 오류나 확인 중으로」 — 기획 미정 항목에서 값이 늘 수 있다).
//
// ⚠️ 시리얼은 요청 **본문에만** 싣는다(URL·쿼리 금지 — 접근 로그·GA 에 남는다). 입력값은 trim 만 하고 정규화는 서버가 한다.
import type { MyCoupon } from "@/libs/coupon/issued-coupon";

export const TRADE_IN_CAMPAIGN_STATES = [
  "UPCOMING",
  "OPEN",
  "PAUSED",
  "ENDED",
] as const;
export type TradeInCampaignState = (typeof TRADE_IN_CAMPAIGN_STATES)[number];

export const TRADE_IN_VERIFICATION_RESULTS = [
  "VERIFIED",
  "NOT_VERIFIED",
  "INVALID_FORMAT",
  "NOT_ELIGIBLE_MODEL",
  "ALREADY_TRADED_IN",
  "RESERVED_BY_OTHER",
  "EXISTING_CLAIM",
  "ACCOUNT_LIMIT_REACHED",
  "CAMPAIGN_NOT_OPEN",
] as const;
export type TradeInVerificationResult =
  (typeof TRADE_IN_VERIFICATION_RESULTS)[number];

export const TRADE_IN_ISSUE_RESULTS = [
  "ISSUED",
  "EXISTING_CLAIM",
  "VERIFICATION_EXPIRED",
  "TERMS_OUTDATED",
  "NOT_VERIFIED",
  "ALREADY_TRADED_IN",
  "RESERVED_BY_OTHER",
  "ACCOUNT_LIMIT_REACHED",
  "CAMPAIGN_NOT_OPEN",
] as const;
export type TradeInIssueResult = (typeof TRADE_IN_ISSUE_RESULTS)[number];

export const TRADE_IN_CLAIM_STATUSES = [
  "ISSUED",
  "CONFIRMED",
  "EXPIRED",
  "ON_HOLD",
  "REVOKED",
] as const;
/** 모르는 상태는 `UNKNOWN` — 화면은 「확인 중 — 고객지원」으로 그린다(계약 v0). */
export type TradeInClaimStatus =
  (typeof TRADE_IN_CLAIM_STATUSES)[number] | "UNKNOWN";

/** `GET /api/v1/trade-in/campaign` — 공개. 분기는 `state` 하나로만 한다. 시각은 epoch ms(UTC) · 표시용. */
export type TradeInCampaign = {
  campaignId: string;
  state: TradeInCampaignState;
  claimableFrom: number;
  claimableUntil: number;
  couponValidUntil: number;
  percent: number;
  targetSku: string;
  /** ISO-2, `[]` = 제한 없음. */
  eligibleCountries: string[];
  /** 발급 요청에 그대로 싣는다 — 다르면 `TERMS_OUTDATED`. */
  termsVersion: string;
};

export type TradeInDevice = {
  /** "TactSuit X40" · "TactSuit X16" (원장 기준). */
  model: string;
  /** 정규화한 전체 시리얼. 본인 확인된 기기에만 내려온다. */
  serialNumber: string;
  /** 서버가 가린 값 — 그대로 표시한다. */
  maskedSerial: string;
};

export type TradeInClaim = {
  id: string;
  /** 표시·CS 문의용 번호. 입력해서 쓰는 쿠폰 코드가 아니다(쿠폰은 계정에 붙어 자동 적용된다). */
  code: string;
  status: TradeInClaimStatus;
  device: TradeInDevice;
  coupon: MyCoupon | null;
  /** `CONFIRMED` 일 때 주문번호, 아니면 "". */
  orderId: string;
  issuedAt: number;
  /** 0 = 없음. */
  confirmedAt: number;
  /** 0 = 없음. */
  expiredAt: number;
  canReapply: boolean;
};

export type TradeInVerification = {
  result: TradeInVerificationResult;
  /** 소유 확인을 통과한 결과에만. `NOT_ELIGIBLE_MODEL` 은 null. */
  device: TradeInDevice | null;
  /** `VERIFIED` 만. 30분 유효 · 이 계정·이 시리얼 전용. */
  verificationToken: string | null;
  tokenExpiresAt: number;
  /** `VERIFIED` 만 — 최종 확인창. */
  offer: { percent: number; targetSku: string; validUntil: number } | null;
  /** `EXISTING_CLAIM` 만. */
  claim: TradeInClaim | null;
};

export type TradeInIssue = {
  result: TradeInIssueResult;
  claim: TradeInClaim | null;
};

export type TradeInConsents = {
  ownership: boolean;
  repairRestriction: boolean;
  noResale: boolean;
};

/**
 * HTTP 를 거친 한 번의 호출 결과. 업무 결과(확인·발급의 `result`)는 `ok` 안에 있고, 나머지는 HTTP 층의 사정이다
 * (mgmt docs/trade-in.md 「API」 — 401·400·429·503·5xx 만 쓴다).
 *
 * - `unauthorized` 401 — 로그인 만료. 다시 로그인시킨다.
 * - `rate-limited` 429 — 계정당 1시간 실패 상한. `retryAfterSeconds` 동안 입력을 막는다(헤더가 없거나 못 읽으면 null).
 * - `unavailable` 503 `trade-in-verification-unavailable` — 소유 확인을 **할 수 없다**(스냅샷 없음·장애). 「확인 안 됨」이
 *   아니라 「잠시 후 다시」다.
 * - `error` — 그 밖 전부(400 클라이언트 버그 · 5xx · 네트워크 · 본문이 계약과 다름 · 모르는 `result`). 일반 오류로 그린다.
 */
export type TradeInOutcome<T> =
  | { kind: "ok"; data: T }
  | { kind: "unauthorized" }
  | { kind: "rate-limited"; retryAfterSeconds: number | null }
  | { kind: "unavailable" }
  | { kind: "error"; status: number | null; reason: string };

export const TRADE_IN_UNAVAILABLE_REASON = "trade-in-verification-unavailable";

/** `Retry-After` — mgmt 는 초 단위 정수를 보낸다. HTTP-date 형식이나 이상한 값은 null(모름)로 둔다. */
export function parseRetryAfterSeconds(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value.trim())) return null;
  return Number(value.trim());
}

/**
 * HTTP 상태·본문을 결과로 바꾼다. 200 이면 `parse` 로 `message` 를 읽고, 계약과 다르면(`parse` 가 null) 일반 오류다.
 * `fetch` 는 부르지 않는다 — 호출처가 응답을 넘긴다(테스트에서 응답만 흉내 내면 된다).
 */
export function toTradeInOutcome<T>(
  status: number,
  body: unknown,
  retryAfter: string | null,
  parse: (message: unknown) => T | null,
): TradeInOutcome<T> {
  const errorMessage = readString(body, "errorMessage");
  if (status === 401) return { kind: "unauthorized" };
  if (status === 429) {
    return {
      kind: "rate-limited",
      retryAfterSeconds: parseRetryAfterSeconds(retryAfter),
    };
  }
  if (status === 503 && errorMessage === TRADE_IN_UNAVAILABLE_REASON) {
    return { kind: "unavailable" };
  }
  if (status < 200 || status >= 300) {
    return { kind: "error", status, reason: errorMessage ?? "http-error" };
  }
  const data = parse(isRecord(body) ? body.message : undefined);
  if (data === null) {
    return { kind: "error", status, reason: "unexpected-body" };
  }
  return { kind: "ok", data };
}

// ── 본문 해석 — 계약의 필수 필드가 없거나 열거값을 모르면 null(일반 오류) ───────────────────────────────

export function parseTradeInCampaign(message: unknown): TradeInCampaign | null {
  if (!isRecord(message)) return null;
  const state = oneOf(message.state, TRADE_IN_CAMPAIGN_STATES);
  if (state === null) return null;
  return {
    campaignId: str(message.campaignId),
    state,
    claimableFrom: num(message.claimableFrom),
    claimableUntil: num(message.claimableUntil),
    couponValidUntil: num(message.couponValidUntil),
    percent: num(message.percent),
    targetSku: str(message.targetSku),
    eligibleCountries: Array.isArray(message.eligibleCountries)
      ? message.eligibleCountries.filter(
          (c): c is string => typeof c === "string",
        )
      : [],
    termsVersion: str(message.termsVersion),
  };
}

export function parseTradeInVerification(
  message: unknown,
): TradeInVerification | null {
  if (!isRecord(message)) return null;
  const result = oneOf(message.result, TRADE_IN_VERIFICATION_RESULTS);
  if (result === null) return null;
  const verificationToken =
    typeof message.verificationToken === "string" &&
    message.verificationToken !== ""
      ? message.verificationToken
      : null;
  // 계약상 짝이 맞아야 다음 단계로 갈 수 있다 — `VERIFIED` 인데 토큰이 없으면 발급할 수 없고, `EXISTING_CLAIM` 인데
  // 신청이 없으면 그 상태 화면을 그릴 수 없다. 둘 다 일반 오류로 둔다.
  const claim = message.claim == null ? null : parseTradeInClaim(message.claim);
  if (result === "VERIFIED" && verificationToken === null) return null;
  if (result === "EXISTING_CLAIM" && claim === null) return null;
  const offer = isRecord(message.offer)
    ? {
        percent: num(message.offer.percent),
        targetSku: str(message.offer.targetSku),
        validUntil: num(message.offer.validUntil),
      }
    : null;
  return {
    result,
    device: parseDevice(message.device),
    verificationToken,
    tokenExpiresAt: num(message.tokenExpiresAt),
    offer,
    claim,
  };
}

export function parseTradeInIssue(message: unknown): TradeInIssue | null {
  if (!isRecord(message)) return null;
  const result = oneOf(message.result, TRADE_IN_ISSUE_RESULTS);
  if (result === null) return null;
  const claim = message.claim == null ? null : parseTradeInClaim(message.claim);
  // 발급됐거나 이미 받은 신청이면 그 신청을 그려야 한다 — 없으면 일반 오류(새로고침하면 `GET claims` 가 복구한다).
  if ((result === "ISSUED" || result === "EXISTING_CLAIM") && claim === null) {
    return null;
  }
  return { result, claim };
}

/** 내 참여 내역(최신순). 한 건이라도 못 읽으면 목록 전체를 일반 오류로 둔다 — 빠진 채 그리면 「참여 안 함」으로 보인다. */
export function parseTradeInClaims(message: unknown): TradeInClaim[] | null {
  if (!Array.isArray(message)) return null;
  const claims = message.map(parseTradeInClaim);
  return claims.every((c): c is TradeInClaim => c !== null) ? claims : null;
}

export function parseTradeInClaim(value: unknown): TradeInClaim | null {
  if (!isRecord(value)) return null;
  const device = parseDevice(value.device);
  if (str(value.id) === "" || device === null) return null;
  return {
    id: str(value.id),
    code: str(value.code),
    status: oneOf(value.status, TRADE_IN_CLAIM_STATUSES) ?? "UNKNOWN",
    device,
    coupon: isRecord(value.coupon) ? (value.coupon as MyCoupon) : null,
    orderId: str(value.orderId),
    issuedAt: num(value.issuedAt),
    confirmedAt: num(value.confirmedAt),
    expiredAt: num(value.expiredAt),
    canReapply: value.canReapply === true,
  };
}

function parseDevice(value: unknown): TradeInDevice | null {
  if (!isRecord(value)) return null;
  return {
    model: str(value.model),
    serialNumber: str(value.serialNumber),
    maskedSerial: str(value.maskedSerial),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(body: unknown, key: string): string | null {
  return isRecord(body) && typeof body[key] === "string"
    ? (body[key] as string)
    : null;
}

function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | null {
  return typeof value === "string" &&
    (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
